import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type { AISettingsDataDTO } from './AIAnalyst.types';

const aiSettingsQueryKey = (orgLabel: string) =>
  `organization_settings/${orgLabel}/ai_settings`;

export const useFetchAISettings = (orgLabel: string) => {
  const { data, isLoading, refetch } = useQuery<AISettingsDataDTO>({
    queryKey: [aiSettingsQueryKey(orgLabel)],
    enabled: !!orgLabel && orgLabel !== 'all',
    staleTime: Infinity
  });

  return {
    data: data ?? {
      ai_enable_submission: undefined,
      ai_disable_anthropic_models: undefined,
      ai_org_instructions: null,
      ai_ticket_response_instructions: null,
      ai_communication_level: 'default',
      recent_logs: []
    },
    isLoading,
    refetch
  };
};

export const useMutateAISettings = (orgLabel: string) => {
  const queryClient = useQueryClient();
  const handleError = useHandleError();

  return useMutation({
    mutationFn: (payload: {
      ai_enable_submission?: boolean | null;
      ai_disable_anthropic_models?: boolean | null;
      ai_org_instructions?: string | null;
      ai_ticket_response_instructions?: string | null;
      ai_communication_level?: string | null;
      message?: string | null;
    }) =>
      axiosClient.put(
        `/organization_settings/${orgLabel}/ai_settings`,
        payload
      ),
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to update AI settings.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [aiSettingsQueryKey(orgLabel)]
      });
      // organizationsAtom's 10-minute-stale query embeds ai_enable_submission; refresh it so Kanati category gating stays in sync
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].startsWith('organizations/products/')
      });
      enqueueSnackbar('AI settings updated', { variant: 'success' });
    }
  });
};
